import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ptBR from './locales/pt-BR.json';
import enUS from './locales/en-US.json';
import { ROLE_DISPLAY_PT_BR, ROLE_DISPLAY_EN_US } from '../rbac/roles';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      'pt-BR': { translation: ptBR },
      'en-US': { translation: enUS },
    },
    fallbackLng: 'pt-BR',
    // Official opt-out (i18next v23.16+) for the "i18next is made possible by
    // Locize" startup banner — it is emitted via `console.info`, so the old
    // console.log override never caught it. This is the supported flag.
    showSupportNotice: false,
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
  });

/**
 * Wave 3 RBAC sanitization: register a `roles` namespace into the existing
 * translation bundles so we don't have to rewrite the giant pt-BR.json /
 * en-US.json files. Display strings come from the canonical role registry
 * (src/shared/rbac/roles.ts), which is the single source of truth per the
 * governance docx.
 *
 * Usage in components:
 *   const { t } = useTranslation();
 *   t('roles.tenant_admin');  // "Tenant Admin"
 *
 * Prefer `displayRolePtBr(role)` from the rbac module when you already
 * have the raw role string in hand — it canonicalizes legacy aliases too.
 */
function registerRolesNamespace(): void {
  i18n.addResourceBundle(
    'pt-BR',
    'translation',
    { roles: ROLE_DISPLAY_PT_BR },
    /* deep */ true,
    /* overwrite */ true,
  );
  i18n.addResourceBundle(
    'en-US',
    'translation',
    { roles: ROLE_DISPLAY_EN_US },
    /* deep */ true,
    /* overwrite */ true,
  );
}
registerRolesNamespace();

/**
 * Sync i18n language with the persisted languagePreference from useAppStore.
 * Called once at app startup (see main.tsx or App.tsx) and whenever the
 * user changes their language preference in the profile page.
 *
 * This allows Zustand (the source of truth for language preference) to drive
 * the i18next instance without creating a circular dependency between the
 * store and the i18n initialisation module.
 */
export function syncLanguageFromStore(languagePreference: string): void {
  if (languagePreference && i18n.language !== languagePreference) {
    i18n.changeLanguage(languagePreference);
  }
}

export default i18n;
